import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "react-query";
import format from "date-fns/format";
import debounce from "lodash/debounce";

import {
  AutoComplete,
  Button,
  Dialog,
  FormControlMessage,
} from "fe-shared/src/components";
import type { AutoCompleteOption } from "fe-shared/src/components";
import { PickupRequestMaterial } from "fe-shared-requests/src/types";
import { formatWeight } from "fe-shared-requests/src/helpers/materials";
import {
  formatRequestNumber,
  formatRequestNumber as formatTransactionNumber,
} from "fe-shared-requests/src/helpers/format";

import * as api from "./modules/api";

import styles from "./PickupRequestLinkTransactionModal.module.scss";

export interface Props {
  isActive: boolean;
  requestNumber: string;
  collectorName: string;
  materials: PickupRequestMaterial[];
  isSubmitting?: boolean;
  onConfirm: (transactionNumber: string) => void;
  onCancel: () => void;
}

const DATE_FORMAT = "MMM d, yyyy";

const PickupRequestLinkTransactionModal = (props: Props) => {
  const {
    isActive,
    requestNumber,
    collectorName,
    materials,
    isSubmitting = false,
    onConfirm,
    onCancel,
  } = props;
  const { t } = useTranslation();
  const [selected, setSelected] = useState<AutoCompleteOption | null>(null);
  const [search, setSearch] = useState("");

  // eslint-disable-next-line
  const debouncedSetSearch = useCallback(debounce(setSearch, 300), []);

  useEffect(() => {
    if (isActive) {
      setSelected(null);
      setSearch("");
    }

    // Without this, a search typed just before closing lands after the reset and comes back
    // with the dialog.
    return () => debouncedSetSearch.cancel();
  }, [isActive, debouncedSetSearch]);

  // Only the latest N pickups come back from the server - a search hit outside that set can
  // never appear by filtering the already-fetched list, so the query must reach the server.
  const linkableQuery = useQuery(
    ["pickupRequestLinkableTransactions", requestNumber, search],
    () => api.getLinkableTransactions(requestNumber, search),
    // The dialog offers its own Retry, so three silent attempts would only delay the message.
    { enabled: isActive, retry: false }
  );

  const transactions = useMemo(
    () => linkableQuery.data?.transactions ?? [],
    [linkableQuery.data]
  );

  const options: AutoCompleteOption[] = useMemo(
    () =>
      transactions.map((transaction) => {
        const materialsSummary = transaction.materials
          .map(
            (material) =>
              `${t(`pickupRequests.materials.codes.${material.code}`, {
                defaultValue: material.code,
              })} ${formatWeight(material.quantity)}`
          )
          .join(", ");
        // An empty materials array is the "row survived, its items didn't" gap the SP's inner
        // join can produce - a "0" total there would misreport a pickup as literally empty.
        const totalUnits = transaction.materials.length
          ? formatWeight(
              transaction.materials.reduce((sum, material) => sum + material.quantity, 0)
            )
          : "";

        return {
          value: transaction.pickupTransactionNumber,
          label: formatTransactionNumber(transaction.pickupTransactionNumber),
          description: [
            materialsSummary,
            totalUnits,
            format(new Date(transaction.completedDate), DATE_FORMAT),
            transaction.haulerName,
            transaction.shipmentTransactionNumber &&
              formatTransactionNumber(transaction.shipmentTransactionNumber),
          ]
            .filter(Boolean)
            .join(" · "),
        };
      }),
    [transactions, t]
  );

  const totalQuantity = materials.reduce(
    (sum, material) => sum + material.quantity,
    0
  );

  const hintText = linkableQuery.data
    ? t("pickupRequests.details.linkTransactionHint", {
        collectorName,
        limit: linkableQuery.data.limit,
      })
    : undefined;

  const handleConfirm = () => {
    if (!selected) {
      return;
    }

    onConfirm(String(selected.value));
  };

  const renderTransactionPicker = () => {
    if (linkableQuery.isError) {
      return (
        <div className={styles.empty}>
          <span className={styles.emptyMessage}>
            {t("pickupRequests.details.linkTransactionLoadError")}
          </span>
          <Button
            id="link-transaction-retry"
            variant="outlined"
            disabled={linkableQuery.isFetching}
            onClick={() => linkableQuery.refetch()}
          >
            {t("pickupRequests.details.linkTransactionRetry")}
          </Button>
        </div>
      );
    }

    if (!linkableQuery.isLoading && !transactions.length && !search) {
      return (
        <div className={styles.empty}>
          <span className={styles.emptyMessage}>
            {t("pickupRequests.details.linkTransactionEmpty")}
          </span>
          <FormControlMessage id="link-transaction-empty" hintText={hintText} />
        </div>
      );
    }

    return (
      <AutoComplete
        id="link-transaction-picker"
        title={t("pickupRequests.details.linkTransactionFieldLabel")}
        required
        options={options}
        value={selected ?? undefined}
        placeholder={t("pickupRequests.details.linkTransactionPlaceholder")}
        isLoading={linkableQuery.isLoading}
        isEmptyStateVisible
        disabled={isSubmitting}
        hintText={hintText}
        tooltipClassName={styles.linkTransactionDropdown}
        onChange={(_event, value) => {
          const found = options.find((option) => option.value === value);

          setSelected(found ?? null);
        }}
        onTextChange={(_event, value) => {
          // Typing past a selection must not leave Link submitting the transaction that is no
          // longer on screen.
          if (selected && value !== selected.label) {
            setSelected(null);
          }

          debouncedSetSearch(value.replace(/[#\s]/g, ""));
        }}
      />
    );
  };

  return (
    <Dialog
      modal
      width={608}
      isActive={isActive}
      onClose={onCancel}
      title={(
        <span>
          <span>{t("pickupRequests.details.linkTransactionTitle")}</span>
          {" "}
          <span>{formatRequestNumber(requestNumber)}</span>
        </span>
      )}
      actions={[
        <Button
          id="link-transaction-modal-confirm"
          key="confirm"
          disabled={isSubmitting || !selected}
          onClick={handleConfirm}
        >
          {t("pickupRequests.details.linkTransactionConfirm")}
        </Button>,
        <Button
          id="link-transaction-modal-cancel"
          key="cancel"
          variant="outlined"
          disabled={isSubmitting}
          onClick={onCancel}
        >
          {t("pickupRequests.details.cancel")}
        </Button>,
      ]}
    >
      <div className={styles.materialsBlock}>
        <div className={styles.materialsHeading}>
          {t("pickupRequests.details.linkTransactionMaterialsHeading")}
        </div>
        <div className={styles.materialsGrid}>
          <span className={styles.materialsTotal}>
            {t("pickupRequests.details.totalToPickup")}
          </span>
          <span className={styles.materialsTotal}>{totalQuantity}</span>
          {materials.map((material) => (
            <React.Fragment key={material.code}>
              <span className={styles.materialLabel}>
                {material.name || material.code}
              </span>
              <span className={styles.materialValue}>{material.quantity}</span>
            </React.Fragment>
          ))}
        </div>
      </div>
      {renderTransactionPicker()}
    </Dialog>
  );
};

export default PickupRequestLinkTransactionModal;
